import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useWorkspace } from "@/context/WorkspaceContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Search, Loader2, Download, FilePlus2, Check, Linkedin, Facebook, Globe, History, CalendarDays, BarChart3, Megaphone, CalendarRange } from "lucide-react";
import DraftBuilderModal from "@/components/content/DraftBuilderModal";
import PPCModal from "@/components/content/PPCModal";
import BlogCampaignModal from "@/components/content/BlogCampaignModal";
import { useAuth } from "@/context/AuthContext";
import { KeywordUpgradeModal, useKeywordCredits } from "@/components/KeywordCredits";

const PLAT = {
  LinkedIn: { icon: Linkedin, cls: "text-[#5aa3ea] border-[#0A66C2]/40 bg-[#0A66C2]/10" },
  Facebook: { icon: Facebook, cls: "text-[#6fa3ff] border-[#1877F2]/40 bg-[#1877F2]/10" },
  General: { icon: Globe, cls: "text-muted-foreground border-border bg-secondary/40" },
};
const POP = { High: "text-emerald-300 border-emerald-500/40 bg-emerald-500/10", Medium: "text-sky-300 border-sky-500/40 bg-sky-500/10", "Long-Tail": "text-amber-300 border-amber-500/40 bg-amber-500/10" };

const csvCell = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
const downloadCsv = (seed, rows) => {
  const head = ["Keyword / Phrase", "Recommended Platform", "Search Type", "Popularity", "Monthly Searches", "Competition", "CPC (USD)", "Content Idea Angle"];
  const body = rows.map((r) => [r.keyword, r.platform, r.search_type, r.popularity, r.volume ?? "", r.competition ?? "", r.cpc ?? "", r.angle].map(csvCell).join(","));
  const blob = new Blob([[head.map(csvCell).join(","), ...body].join("\n")], { type: "text/csv" });
  const a = document.createElement("a");
  a.href = URL.createObjectURL(blob);
  a.download = `keywords-${seed.replace(/\W+/g, "-")}.csv`;
  a.click();
  URL.revokeObjectURL(a.href);
};

function KeywordRow({ r, onDraft, state }) {
  const p = PLAT[r.platform] || PLAT.General;
  const Icon = p.icon;
  return (
    <tr className="border-t border-border hover:bg-secondary/20 transition-colors" data-testid={`kw-row-${r.id}`}>
      <td className="py-3 px-4 font-medium text-sm">{r.keyword}</td>
      <td className="py-3 px-4">
        <span className={`inline-flex items-center gap-1.5 text-xs px-2 py-1 rounded-md border ${p.cls}`}><Icon className="h-3 w-3" /> {r.platform}</span>
      </td>
      <td className="py-3 px-4">
        <span className={`text-[11px] px-2 py-1 rounded-md border whitespace-nowrap ${POP[r.popularity] || POP["Long-Tail"]}`} data-testid={`kw-type-${r.id}`}>{r.search_type} · {r.popularity}</span>
      </td>
      <td className="py-3 px-4 text-xs font-mono whitespace-nowrap" data-testid={`kw-volume-${r.id}`}>
        {typeof r.volume === "number" ? r.volume.toLocaleString() : r.looked_up ? <span className="text-muted-foreground">no data</span> : "—"}
        {(r.competition || typeof r.cpc === "number") && (
          <span className="block text-[10px] text-muted-foreground">{r.competition ? `${r.competition.toLowerCase()} comp` : ""}{typeof r.cpc === "number" ? ` · $${r.cpc.toFixed(2)}` : ""}</span>
        )}
      </td>
      <td className="py-3 px-4 text-xs text-muted-foreground max-w-[340px]">{r.angle}</td>
      <td className="py-3 px-4 text-right">
        <Button size="sm" variant="outline" onClick={() => onDraft(r)}
          data-testid={`kw-draft-btn-${r.id}`} className="h-8 text-xs border-border whitespace-nowrap">
          {state === "done" ? <Check className="h-3.5 w-3.5 mr-1.5 text-emerald-400" /> : <FilePlus2 className="h-3.5 w-3.5 mr-1.5" />}
          {state === "done" ? "Drafted" : "Add to Post Draft"}
        </Button>
      </td>
    </tr>
  );
}

export default function KeywordResearch() {
  const { active } = useWorkspace();
  const navigate = useNavigate();
  const [seed, setSeed] = useState("");
  const [busy, setBusy] = useState(false);
  const [data, setData] = useState(null);
  const [drafts, setDrafts] = useState({});
  const [history, setHistory] = useState([]);
  const { user } = useAuth();
  const [credits, setCredits] = useKeywordCredits();
  const [volBusy, setVolBusy] = useState(false);
  const [upgrade, setUpgrade] = useState(null);
  const [draftRow, setDraftRow] = useState(null);
  const [ppcOpen, setPpcOpen] = useState(false);
  const [campOpen, setCampOpen] = useState(false);
  const kwMetrics = () => Object.fromEntries((data?.rows || []).map((x) => [x.keyword, { volume: x.volume, cpc: x.cpc, competition: x.competition }]));
  const isAgency = user?.is_admin || user?.plan === "agency";

  const handleErr = (e, fallback) => {
    const d = e?.response?.data?.detail;
    if (e?.response?.status === 403 && d && typeof d === "object") {
      setUpgrade({ kind: d.code === "keyword_draft_tier" ? "draft" : "credits", message: d.message || fallback });
      if (d.credits) setCredits(d.credits);
      return true;
    }
    toast.error(typeof d === "string" ? d : (d?.message || fallback));
    return false;
  };

  const getVolumes = async () => {
    if (!data) return;
    setVolBusy(true);
    try {
      const r = await api.post("/keywords/volume", { keywords: data.rows.map((x) => x.keyword) });
      setCredits(r.data.credits);
      const v = r.data.volumes || {};
      const rows = data.rows.map((x) => {
        const m = v[x.keyword];
        if (!m) return x;
        x = { ...x, looked_up: true };
        const vol = m.volume;
        const popularity = typeof vol === "number" ? (vol >= 1000 ? "High" : vol >= 100 ? "Medium" : "Long-Tail") : x.popularity;
        return { ...x, volume: vol, competition: m.competition, cpc: m.cpc, popularity, search_type: popularity === "High" ? "High Volume Seed" : "Long-Tail Keyword" };
      }).sort((a, b) => (b.volume ?? -1) - (a.volume ?? -1));
      setData({ ...data, rows, volume_source: r.data.status });
      if (r.data.status === "live" || r.data.status === "cached") toast.success(r.data.charged ? "Exact volumes loaded · 1 credit used" : "Exact volumes loaded from cache · no credit used");
      else toast.error("Search volumes are unavailable right now — showing autocomplete ranking. No credit used.");
    } catch (e) { handleErr(e, "Could not load search volumes"); }
    finally { setVolBusy(false); }
  };

  const exportCsv = () => {
    if (!isAgency) { setUpgrade({ kind: "agency", message: "CSV export and team sharing are on the Agency plan ($199/mo)." }); return; }
    downloadCsv(data.seed, data.rows);
  };

  useEffect(() => { api.get("/keywords/history").then((r) => setHistory(r.data)).catch(() => {}); }, [data]);

  const run = async (q) => {
    const s = (q ?? seed).trim();
    if (s.length < 2) { toast.error("Enter a keyword or topic"); return; }
    setSeed(s); setBusy(true); setDrafts({});
    try {
      setData((await api.post("/keywords/research", { seedKeyword: s, workspace_id: active?.id })).data);
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : (d?.message || "Keyword research failed"), d?.code === "keyword_cap" ? { action: { label: "See plans", onClick: () => navigate("/billing") } } : undefined);
    } finally { setBusy(false); }
  };

  const draft = (r) => {
    if (!active) return;
    if (!(user?.is_admin || ["growth", "agency"].includes(user?.plan))) {
      setUpgrade({ kind: "draft", message: "Sending keywords to the Facebook & LinkedIn draft builder is on E-Commerce ($79/mo) and Agency." });
      return;
    }
    setDraftRow(r);
  };

  return (
    <div className="p-4 sm:p-8 max-w-7xl mx-auto space-y-6" data-testid="keyword-research-page">
      <div>
        <h1 className="text-3xl font-display font-bold tracking-tight">Keyword Research</h1>
        <p className="text-sm text-muted-foreground mt-1">Real long-tail searches from Google Autocomplete, mapped to Facebook or LinkedIn with a post angle for each.</p>
      </div>
      <form onSubmit={(e) => { e.preventDefault(); run(); }} className="flex gap-2 flex-col sm:flex-row" data-testid="kw-search-form">
        <div className="relative flex-1">
          <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input value={seed} onChange={(e) => setSeed(e.target.value)} placeholder="e.g. sustainable sneakers, shopify email marketing"
            data-testid="kw-seed-input" className="pl-9 h-11 bg-card border-border" maxLength={80} />
        </div>
        <Button type="submit" disabled={busy} data-testid="kw-generate-btn" className="h-11 px-6 bg-primary hover:bg-primary/90 text-white">
          {busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Search className="h-4 w-4 mr-2" />} Generate Keywords
        </Button>
      </form>
      {history.length > 0 && !data && (
        <div className="flex items-center gap-2 flex-wrap text-xs" data-testid="kw-history">
          <History className="h-3.5 w-3.5 text-muted-foreground" />
          {history.slice(0, 8).map((h) => (
            <button key={h.search_id} onClick={() => run(h.seed)} className="rounded-full border border-border px-2.5 py-1 hover:border-primary/50 transition-colors">{h.seed}</button>
          ))}
        </div>
      )}
      {data && (
        <div className="rounded-xl border border-border bg-card overflow-hidden" data-testid="kw-results">
          <div className="flex items-center justify-between px-4 py-3 border-b border-border gap-3 flex-wrap">
            <p className="text-sm"><span className="font-semibold">{data.rows.length}</span> keywords for <span className="font-semibold">"{data.seed}"</span>
              <span className="ml-2 text-[11px] text-muted-foreground" data-testid="kw-volume-source">
                {["live", "cached"].includes(data.volume_source) ? "· US Google Ads volumes via DataForSEO"
                  : data.volume_source === "partial" ? "· some volumes from cache — get exact volumes for the rest"
                  : "· ranked by Google Autocomplete position"}
              </span>
            </p>
            <div className="flex gap-2">
              <Button size="sm" variant="outline" onClick={() => navigate("/calendar")} data-testid="kw-open-plan" className="border-border"><CalendarDays className="h-3.5 w-3.5 mr-1.5" /> Post plan</Button>
              <Button size="sm" variant="outline" onClick={() => setCampOpen(true)} data-testid="kw-blog-campaign-btn" className="border-primary/40 text-primary"><CalendarRange className="h-3.5 w-3.5 mr-1.5" /> Daily blog campaign</Button>
              <Button size="sm" variant="outline" onClick={() => setPpcOpen(true)} data-testid="kw-ppc-btn" className="border-border"><Megaphone className="h-3.5 w-3.5 mr-1.5" /> PPC campaign</Button>
              {!["live", "cached"].includes(data.volume_source) || data.rows.some((x) => x.volume == null && !x.looked_up) ? (
                <Button size="sm" onClick={getVolumes} disabled={volBusy} data-testid="kw-get-volumes" className="bg-primary hover:bg-primary/90 text-white">
                  {volBusy ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <BarChart3 className="h-3.5 w-3.5 mr-1.5" />}
                  Exact volumes · 1 credit{credits ? ` (${credits.available} left)` : ""}
                </Button>
              ) : null}
              <Button size="sm" variant="outline" onClick={exportCsv} data-testid="kw-export-csv" className="border-border"><Download className="h-3.5 w-3.5 mr-1.5" /> Export CSV</Button>
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-left min-w-[820px]">
              <thead className="text-[11px] uppercase tracking-wider text-muted-foreground bg-secondary/30">
                <tr><th className="py-2.5 px-4">Keyword / Phrase</th><th className="py-2.5 px-4">Recommended Platform</th><th className="py-2.5 px-4">Search Type</th><th className="py-2.5 px-4">Monthly Searches</th><th className="py-2.5 px-4">Content Idea Angle</th><th className="py-2.5 px-4 text-right">Action</th></tr>
              </thead>
              <tbody>{data.rows.map((r) => <KeywordRow key={r.id} r={r} onDraft={draft} state={drafts[r.id]} />)}</tbody>
            </table>
          </div>
        </div>
      )}
      {active && data && (
        <>
          <DraftBuilderModal open={!!draftRow} onOpenChange={(o) => !o && setDraftRow(null)} row={draftRow} workspace={active}
            onUpgrade={(u) => { setDraftRow(null); setUpgrade(u); }} onDone={(r) => setDrafts((d) => ({ ...d, [r.id]: "done" }))} />
          <PPCModal open={ppcOpen} onOpenChange={setPpcOpen} keywords={data.rows.map((x) => x.keyword)} metrics={kwMetrics()} workspace={active}
            onUpgrade={(u) => { setPpcOpen(false); setUpgrade(u); }} />
          <BlogCampaignModal open={campOpen} onOpenChange={setCampOpen} keywords={data.rows.map((x) => x.keyword)} metrics={kwMetrics()} workspace={active}
            onUpgrade={(u) => { setCampOpen(false); setUpgrade(u); }} />
        </>
      )}
      <KeywordUpgradeModal open={!!upgrade} onOpenChange={(o) => !o && setUpgrade(null)} message={upgrade?.message} kind={upgrade?.kind} />
    </div>
  );
}
